import { useState } from "react";

interface Props {
  onClose: () => void;
  onDonate: () => void;
}

const presets = ["10", "20", "50", "100", "200"];

const impactMap: Record<string, string> = {
  "10": "1 week of nutritious milk for one infant",
  "20": "A full set of warm clothes for one baby",
  "50": "One month of basic medical care",
  "100": "Safe shelter and care for one infant for a month",
  "200": "Full care package for two infants for a month",
};

export default function DonationModal({ onClose, onDonate }: Props) {
  const [amount, setAmount] = useState("20");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");

  const impact = impactMap[amount] || "Help provide essentials for infants in need";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 overflow-y-auto py-6">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md my-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 flex items-center justify-center bg-rose-100 rounded-full">
              <i className="ri-heart-fill text-rose-500 text-lg"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900">Donate for Infants</h3>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 cursor-pointer">
            <i className="ri-close-line text-stone-500"></i>
          </button>
        </div>

        {/* Charity image */}
        <div className="w-full h-36 rounded-xl overflow-hidden mb-5">
          <img
            src="https://public.readdy.ai/ai/img_res/0610e9ea51374300a1db83f6a39ada01.jpg"
            alt="Infant in need"
            className="w-full h-full object-cover object-top"
          />
        </div>

        <p className="text-stone-600 text-sm text-center mb-5">
          Your donation provides <strong className="text-rose-600">love, milk, warm clothes, and care</strong> to infants around the world who need our help.
        </p>

        {/* Amount presets */}
        <div className="mb-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">Choose Amount</p>
          <div className="grid grid-cols-5 gap-2 mb-3">
            {presets.map((a) => (
              <button
                key={a}
                onClick={() => setAmount(a)}
                className={`py-2 rounded-lg text-sm font-bold border-2 transition-colors cursor-pointer ${
                  amount === a ? "border-rose-500 bg-rose-50 text-rose-600" : "border-stone-200 text-stone-600 hover:border-rose-300"
                }`}
              >
                ${a}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 border-2 border-stone-200 rounded-lg px-3 py-2 focus-within:border-rose-400">
            <span className="text-stone-500 text-sm font-semibold">$</span>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="flex-1 outline-none text-sm text-gray-900"
              placeholder="Custom amount"
              min="1"
            />
          </div>
        </div>

        {/* Impact */}
        <div className="bg-rose-50 border border-rose-100 rounded-xl p-3 mb-4 flex items-start gap-2">
          <i className="ri-heart-fill text-rose-400 mt-0.5 flex-shrink-0"></i>
          <p className="text-rose-700 text-xs font-medium">${amount} = {impact}</p>
        </div>

        {/* Optional name */}
        <div className="mb-3">
          <label className="text-sm font-semibold text-gray-700 block mb-1.5">Your Name <span className="text-stone-400 font-normal">(optional)</span></label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Show your name on the donor board"
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-rose-400"
          />
        </div>

        {/* Optional message */}
        <div className="mb-5">
          <label className="text-sm font-semibold text-gray-700 block mb-1.5">Message <span className="text-stone-400 font-normal">(optional)</span></label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 200))}
            placeholder="Leave a message of love for the infants..."
            className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-rose-400 resize-none h-16"
            maxLength={200}
          />
        </div>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-stone-200 text-stone-600 font-semibold rounded-lg text-sm hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Cancel
          </button>
          <button
            onClick={onDonate}
            className="flex-1 py-2.5 bg-rose-500 hover:bg-rose-400 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-heart-fill"></i> Donate ${amount}
          </button>
        </div>

        <p className="text-stone-400 text-xs text-center mt-3">
          100% of your donation goes directly to infant care programs worldwide
        </p>
      </div>
    </div>
  );
}
